import { clearStringValue } from 'Core/data/Helpers'
import { Asset } from './Asset';
import { CatalogProcessor } from './CatalogProcessor';

export class Unit {

  constructor() {
    this.boundGetProcessItem = this.getProcessItem.bind(this);
    // Map to track units with label/icon changes during processing
    this.unitsWithChanges = new Map();
  }

  executeArray = (items, itemsExists) => {
    // Reset the changes map for this execution
    this.unitsWithChanges = new Map();

    // Step 1: Process units normally (changes are tracked in getProcessItem)
    // CatalogProcessor.executeArray returns [success, { entityName: items }]
    const [success, result] = CatalogProcessor.executeArray(
      items, 
      itemsExists, 
      'units', 
      this.boundGetProcessItem, 
      'label'
    );

    if (!success) {
      return [false, result];
    }

    // Step 2: Propagate unit changes to drivers (only if changes were detected)
    const propagatedChanges = this.unitsWithChanges.size > 0
      ? this.propagateUnitChangesToDrivers(itemsExists)
      : {};

    // Step 3: Clear the changes map
    this.unitsWithChanges = new Map();

    // Step 4: Return combined result as [success, { units, drivers }]
    return [true, { ...result, ...propagatedChanges }];
  }

  execute = (item, itemsExists) => {
    // Reset the changes map for this execution
    this.unitsWithChanges = new Map();

    // Step 1: Process unit normally (changes are tracked in getProcessItem)
    // CatalogProcessor.execute returns [success, { entityName: items }]
    const [success, result] = CatalogProcessor.execute(
      item, 
      itemsExists, 
      'units', 
      this.boundGetProcessItem, 
      'label'
    );

    if (!success) {
      return [false, result];
    }

    // Step 2: Propagate unit changes to drivers (only if changes were detected)
    const propagatedChanges = this.unitsWithChanges.size > 0
      ? this.propagateUnitChangesToDrivers(itemsExists)
      : {};

    // Step 3: Clear the changes map
    this.unitsWithChanges = new Map();

    // Step 4: Return combined result as [success, { units, drivers }]
    return [true, { ...result, ...propagatedChanges }];
  }

  /**
   * Propagate unit changes to drivers using the tracked changes
   */
  propagateUnitChangesToDrivers = (itemsExists) => {
    const propagatedChanges = {};

    // Only propagate if there are drivers
    if (!itemsExists.drivers || itemsExists.drivers.length === 0) {
      return propagatedChanges;
    }

    // Update drivers with the changed units
    propagatedChanges.drivers = itemsExists.drivers.map(driver => {
      // If driver doesn't have a unit, skip
      if (!driver.unit || !driver.unit.id) {
        return driver;
      }

      const unitId = String(driver.unit.id);
      
      // If this unit was updated, update the driver's unit reference
      if (this.unitsWithChanges.has(unitId)) {
        const updatedUnitData = this.unitsWithChanges.get(unitId);
        
        return {
          ...driver,
          unit: {
            ...driver.unit,
            label: updatedUnitData.label,
            icon: updatedUnitData.icon
          }
        };
      }

      return driver;
    });

    return propagatedChanges;
  }

  getProcessItem = (item, newItem, itemsExists) => {
    const objAsset = new Asset();
    let tags = item?.tags || [];
    let tagIds = item?.tagIds || '';

    // Priority 1: If newItem has tags array, use it directly
    if (newItem?.tags && Array.isArray(newItem?.tags)) {
      tags = newItem?.tags;
      tagIds = newItem?.tagIds !== undefined ? newItem?.tagIds : item?.tagIds;
    } 
    // Priority 2: If newItem has tagIds, reconstruct tags from catalog
    else if (newItem?.tagIds !== undefined) {
      tagIds = newItem.tagIds;
      tags = CatalogProcessor.reconstructTagsFromIds(tagIds, itemsExists || {});
    }

    // Detect label and icon changes
    const finalLabel = newItem.Label || newItem.label || item.label;
    const hasLabelChange = (newItem.label !== undefined || newItem.Label !== undefined) && 
                           finalLabel !== item.label;
    const hasIconChange = newItem?.icon !== undefined && newItem.icon !== item?.icon;

    const objItem = {
      ...item,
      id: newItem.id || newItem.CUnits_id || item.id,
      label: newItem?.Label || newItem?.label || item?.label,
      esn: (newItem?.USN || newItem?.esn) || item.esn,
      imei: (newItem?.IMEI || newItem?.imei) || item.imei,
      useEcuOdometer: newItem?.useEcuOdometer != undefined
        ? Boolean(newItem?.useEcuOdometer) : newItem?.ecuodometer
          ? newItem?.ecuodometer != undefined && Boolean(newItem?.ecuodometer) : item?.useEcuOdometer
            ? item?.useEcuOdometer != undefined && Boolean(item?.useEcuOdometer) : false,
      offsetEcuEngineOdometer:
        (Object.prototype.hasOwnProperty.call(newItem, 'offsetEcuEngineOdometer'))
          ? (newItem.offsetEcuEngineOdometer == null
            ? null
            : Number(newItem.offsetEcuEngineOdometer).toFixed(2))
          : (item.offsetEcuEngineOdometer == null
            ? null
            : Number(item.offsetEcuEngineOdometer).toFixed(2)),
      offsetEngineOdometer:
        (Object.prototype.hasOwnProperty.call(newItem, 'offsetEngineOdometer'))
          ? (newItem.offsetEngineOdometer == null
            ? null
            : Number(newItem.offsetEngineOdometer).toFixed(2))
          : (item.offsetEngineOdometer == null
            ? null
            : Number(item.offsetEngineOdometer).toFixed(2)),
      useEcuEngineHours: newItem?.useEcuEngineHours || item?.useEcuEngineHours || false,
      offsetEcuEngineHours:
        (Object.prototype.hasOwnProperty.call(newItem, 'offsetEcuEngineHours'))
          ? (newItem.offsetEcuEngineHours == null
            ? null
            : Number(newItem.offsetEcuEngineHours).toFixed(2))
          : (item.offsetEcuEngineHours == null
            ? null
            : Number(item.offsetEcuEngineHours).toFixed(2)),
      offsetEngineHours:
        (Object.prototype.hasOwnProperty.call(newItem, 'offsetEngineHours'))
          ? (newItem.offsetEngineHours == null
            ? null
            : Number(newItem.offsetEngineHours).toFixed(2))
          : (item.offsetEngineHours == null
            ? null
            : Number(item.offsetEngineHours).toFixed(2)),
      useEcuVin: newItem?.useEcuVin != undefined
        ? Boolean(newItem?.useEcuVin) : newItem?.ecuVin
          ? newItem?.ecuVin != undefined && Boolean(newItem?.ecuVin) : item?.useEcuVin
            ? item?.useEcuVin != undefined && Boolean(item?.useEcuVin) : false,
      jbus: Boolean(Number(newItem?.jbus)) || Boolean(Number(item?.jbus)),
      vbus: Boolean(Number(newItem?.vbus)) || Boolean(Number(item?.vbus)),
      driverId: (newItem?.DID || newItem?.driverId) != undefined ? (newItem?.DID || newItem?.driverId) : item?.driverId,
      driverName: newItem?.driverName != undefined || ((newItem?.DID || newItem?.driverId) != undefined && (newItem?.DID || newItem?.driverId) == 0) ? clearStringValue(newItem?.driverName) : item?.driverName,
      defaultDriverId: newItem?.defaultDriverId != undefined ? newItem?.defaultDriverId : item?.defaultDriverId,
      driverExternalId: (newItem?.driverExternalId || newItem?.externalId) != undefined ? (newItem?.driverExternalId || newItem?.externalId) : item?.driverExternalId,
      asset: newItem?.asset ? objAsset.getProcessItem(item?.asset || {}, newItem?.asset) : item?.asset,
      vehicleStatusId: newItem?.vehicleStatusId == 0 || newItem?.vehiclestatus_id == 0 ? 0 : newItem?.vehiclestatus_id || newItem?.vehicleStatusId || item?.vehicleStatusId,
      hasVideo: newItem?.hasVideo != undefined ? newItem?.hasVideo : item?.hasVideo,
      tagIds: tagIds,
      tags: tags,
      timeZone: newItem?.timeZone != undefined
        ? newItem?.timeZone
        : newItem?.tz_id != undefined
          ? itemsExists?.timeZones?.find(tz => tz.id === newItem.tz_id) || item?.timeZone
          : item?.timeZone,
      starter: newItem?.starter != undefined ? newItem?.starter : item?.starter,
      desiredStarterState: newItem?.desiredStarterState == "Enable" ? newItem?.desiredStarterState : "Disable",
      useEcuSpeed: newItem?.useEcuSpeed || item?.useEcuSpeed || false,
      sdCard: newItem?.sdCard || item?.sdCard || null,
      maintenanceWarningEventsCount: (newItem?.maintenanceWarningEventsCount !== undefined) ? newItem?.maintenanceWarningEventsCount : item?.maintenanceWarningEventsCount,
      isOffline: newItem?.isOffline != undefined ? newItem?.isOffline : item?.isOffline != undefined ? item?.isOffline : false,
      unitVersion: newItem?.unitVersion != undefined ? newItem?.unitVersion : item?.unitVersion,
      odometerEnable: newItem?.unitVersion?.odometerEnable != undefined ? newItem?.unitVersion?.odometerEnable : item?.odometerEnable,
      hoursEnable: newItem?.unitVersion?.hoursEnable != undefined ? newItem?.unitVersion?.hoursEnable : item?.hoursEnable,
      supportedOutputs: newItem?.unitVersion?.supportedOutputs != undefined ? newItem?.unitVersion?.supportedOutputs : item?.supportedOutputs,
      unitVersionId: newItem?.unitVersionId != undefined ? newItem?.unitVersionId : item?.unitVersionId,
      installed: newItem?.installed != undefined ? newItem?.installed : item?.installed,
      linkedUnit: newItem?.linkedUnit != undefined ? newItem?.linkedUnit : item?.linkedUnit,
      typeId: newItem?.typeId != undefined ? newItem?.typeId : item?.typeId,
      deviceCategory: newItem?.deviceCategory != undefined ? newItem?.deviceCategory : item?.deviceCategory,
      linkedMode: newItem?.linkedMode != undefined ? newItem?.linkedMode : item?.linkedMode,
      linkedUnitId: newItem?.linkedUnitId != undefined ? newItem?.linkedUnitId : item?.linkedUnitId,
      manufacturerId: newItem?.manufacturerId != undefined ? newItem?.manufacturerId : item?.manufacturerId,
      manufacturerName: newItem?.manufacturerName != undefined ? newItem?.manufacturerName : item?.manufacturerName,
      driverBehavior: newItem?.driverBehavior != undefined ? newItem?.driverBehavior : item?.driverBehavior,
      unitType: newItem?.unitType != undefined ? newItem?.unitType : item?.unitType,
      icon: newItem?.icon != undefined ? newItem?.icon : item?.icon,
      inputs: newItem?.inputs != undefined ? newItem?.inputs : item?.inputs,
    }

    // Track this unit if label or icon changed
    if (hasLabelChange || hasIconChange) {
      const unitId = String(objItem.id);
      this.unitsWithChanges.set(unitId, {
        id: objItem.id,
        label: objItem.label,
        icon: objItem.icon
      });
    }

    return objItem
  }

}